import { ADD_MORE_PHOTOS_BELOW, ContentKindCard as ContentKindCardModel } from 'utils/socialCampaign/planAndContent'
import { AddOutlined, AddShoppingCartOutlined, AutoAwesomeOutlined, EditOutlined, FileUploadOutlined, LockOutlined } from '@mui/icons-material'
import { CONTENT_KIND_WAYS, ContentKindProgress } from './contentKinds'
import { FC, ReactElement, ReactNode, useId } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import CircularProgress from '@mui/material/CircularProgress'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUICheckCard } from 'components/common/MUICards'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { ProductIcon } from 'components/common/ProductIcon'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import { formatCampaignDay } from 'utils/socialCampaign/campaignStatus'
import styles from './ContentKindCard.module.sass'
import { useTranslation } from 'react-i18next'

interface Props {
  card: ContentKindCardModel
  /** A missing kind being created with AI or ordered; none until those tickets wire it */
  progress?: ContentKindProgress
  /** The purchase flow for this property, opened in a new tab; null when the user cannot order */
  orderHref: string | null
  /** Order chosen, once its new tab opened */
  onOrder?: () => void
  onIncludedChange: (included: boolean) => void
  onCreateWithAi: () => void
  onUpload: () => void
  /**
   * Opens the kind's visuals to keep or leave out each one (ISSUE-3651); without it, or while the viewer's inventory
   * shows none of the kind (not read yet, or every one on an order their seat does not show), the card's body is static
   */
  onOpenVisuals?: () => void
  /** Neither the checkbox nor the visuals button can change the kind (an approval of the drafts is on its way) */
  disabled?: boolean
}

/**
 * One kind of content on one line (prototype v3 `kindCard`): a `MUICheckCard` with the kind's product glyph and,
 * depending on the kind:
 * - photos, always in: tinted, a padlock after the name, "+ Add more" under 6 photos;
 * - a kind in the gallery: its checkbox includes it or leaves it out ("1 video · in 2 posts" or "1 video · not used");
 * - a kind not in the gallery yet: a dashed card naming the post it would add, and its ways in behind "+ Add"
 *   (Create with AI, Order in a new tab, Upload); or, once on its way, a spinner (creating) or an Ordered badge.
 *
 * The state (busy, ordered, empty, locked, on, off) is on the card's own wrapper, `data-state`.
 */
export const ContentKindCard: FC<Props> = ({
  card,
  progress,
  orderHref,
  onOrder,
  onIncludedChange,
  onCreateWithAi,
  onUpload,
  onOpenVisuals,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const ids = `campaign-kind-${useId().replace(/:/g, '')}`
  const hintId = `${ids}-hint`
  const { kind } = card
  const ways = CONTENT_KIND_WAYS[kind]
  const name = t(`plan.kind.${kind}`)
  const newBadge = <MUIBadge size="sm" color="blue" label={t('plan.new')} />

  const title = (
    <span className={styles.title}>
      {name}
      {card.locked && card.available &&
        <span className={styles.lock} role="img" aria-label={t('plan.always_included')}><LockOutlined sx={{ fontSize: '1.4rem' }} /></span>
      }
      {!card.locked && ways.isNew && newBadge}
    </span>
  )

  const addMenu = (more: boolean) => {
    const menuId = `${ids}-add`
    const items: ReactElement[] = []
    if (ways.createWithAi) {
      items.push(
        <MUIDropdownItem key="create-with-ai" title={t('plan.ways.create_with_ai')} icon={<AutoAwesomeOutlined />} onClick={onCreateWithAi}>
          {ways.isNew && <span className={styles.newBadge}>{newBadge}</span>}
        </MUIDropdownItem>
      )
    }
    if (orderHref) {
      items.push(
        <MUIDropdownItem
          key="order"
          title={card.locked ? t('plan.ways.order_shoot') : t('plan.ways.order')}
          icon={<AddShoppingCartOutlined />}
          onClick={onOrder}
          props={{ component: 'a', href: orderHref, target: '_blank', rel: 'noopener noreferrer' }}
        />
      )
    }
    if (ways.upload) {
      items.push(<MUIDropdownItem key="upload" title={t('plan.ways.upload')} icon={<FileUploadOutlined />} onClick={onUpload} />)
    }
    if (!items.length) return null

    return (
      <MUIDropdown
        id={menuId}
        isKeyboardNavigable
        placement="bottom-end"
        button={(isOpen, open) => (
          <MUIButton
            type="secondaryBorder"
            size="sm"
            startIcon={<AddOutlined />}
            aria-label={more ? t('plan.add_more_label') : t(`plan.add_label.${kind}`)}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={isOpen ? menuId : undefined}
            onClick={open}
          >
            {more ? t('plan.add_more') : t('plan.add')}
          </MUIButton>
        )}
      >
        {items}
      </MUIDropdown>
    )
  }

  // ── not in the gallery yet ──
  if (!card.available) {
    const creating = progress?.state === 'creating' && ways.createWithAi
    const ordered = progress?.state === 'ordered' ? progress : null
    let hint: string
    let trail: ReactNode
    if (creating) {
      hint = t(`plan.creating.${kind}`)
      trail = <CircularProgress size="2rem" role="status" aria-label={t('plan.creating_label')} />
    } else if (ordered) {
      hint = [
        t('plan.arrives', { date: formatCampaignDay(ordered.delivery, i18n.language) }),
        ...(ordered.reservedPosts ? [t('plan.reserved', { count: ordered.reservedPosts })] : []),
      ].join(' · ')
      trail = <MUIBadge size="sm" color="blue" label={t('plan.ordered')} />
    } else {
      hint = card.unlocks.length
        ? t('plan.adds', { count: card.unlocks.length, titles: card.unlocks.join(', ') })
        : t('plan.not_in_gallery_hint')
      trail = addMenu(false)
    }

    return (
      <div className={styles.kind} data-kind={kind} data-state={creating ? 'busy' : ordered ? 'ordered' : 'empty'}>
        <MUICheckCard
          showSelectElement={false}
          isDashed
          icon={<ProductIcon kind={ways.icon} size="lg" />}
          title={title}
          description={<span id={hintId} className={styles.hint}>{hint}</span>}
          trailing={trail}
        />
      </div>
    )
  }

  // ── in the gallery ──
  const countText = card.count === null
    ? null
    : card.kept !== null && card.kept < card.count
      ? t(`plan.kept.${kind}`, { count: card.count, kept: card.kept })
      : t(`plan.count.${kind}`, { count: card.count })
  const aiText = card.madeWithAi
    ? card.madeWithAi === card.count ? t('plan.made_with_ai_all') : t('plan.made_with_ai', { count: card.madeWithAi })
    : null
  const used = card.included
  const hint = (used
    ? [countText, aiText, t('plan.in_posts', { count: card.postsUsing })]
    : [card.count === null ? null : t(`plan.count.${kind}`, { count: card.count }), t('plan.not_used')]
  ).filter(Boolean).join(' · ')
  const isTinted = card.locked || used
  const hintNode = <span id={hintId} className={classnames(styles.hint, { [styles.onTint]: isTinted })}>{hint}</span>

  const body = onOpenVisuals && card.count
    ? {
      children: (
        <button
          type="button"
          className={styles.open}
          aria-label={t(`plan.open_visuals.${kind}`)}
          aria-describedby={hintId}
          disabled={disabled}
          onClick={(event) => {
            // the check card's header toggles the kind on any click inside it
            event.stopPropagation()
            onOpenVisuals()
          }}
        >
          <span className={styles.openText}>
            <Typography component="span" variant="text-sm" fontWeight={550} color={GRAY_900}>{title}</Typography>
            <Typography component="span" variant="text-xs" color={GRAY_700}>{hintNode}</Typography>
          </span>
          <span className={styles.edit} aria-hidden="true"><EditOutlined sx={{ fontSize: '1.6rem' }} /></span>
        </button>
      ),
    }
    : { title, description: hintNode }
  const icon = <ProductIcon kind={ways.icon} size="lg" background={isTinted ? 'white' : 'beige'} />

  return (
    <div className={styles.kind} data-kind={kind} data-state={card.locked ? 'locked' : used ? 'on' : 'off'} data-disabled={disabled || undefined}>
      {card.locked
        ? <MUICheckCard
          showSelectElement={false}
          highlighted
          icon={icon}
          trailing={card.count !== null && card.count < ADD_MORE_PHOTOS_BELOW ? addMenu(true) : undefined}
          {...body}
        />
        : <MUICheckCard
          checked={used}
          disabled={disabled}
          onCheck={() => onIncludedChange(!used)}
          aria-label={t(`plan.use.${kind}`)}
          aria-describedby={hintId}
          icon={icon}
          {...body}
        />
      }
    </div>
  )
}
